import { AVATARS_MEN, AVATARS_WOMEN, ZipCityPicker } from '@/entities/profile';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Textarea } from '@/shared/ui/textarea';
import { useTranslate } from '@tolgee/react';
import { useEffect } from 'react';
import { RequiredMark } from './fields';

/** Legacy step4: avatar, description, zipcode + town (geoid). */
export interface ProfileStepData {
  avatarId?: number;
  description: string;
  zipCode: string;
  geoId?: number;
  cityLabel: string;
}

interface StepProfileProps {
  data: ProfileStepData;
  gender: number; // 1 = male, 2 = female
  onChange: (data: Partial<ProfileStepData>) => void;
  onValidChange: (isValid: boolean) => void;
}

export function StepProfile({ data, gender, onChange, onValidChange }: StepProfileProps) {
  const { t } = useTranslate();
  const avatars = gender === 2 ? AVATARS_WOMEN : AVATARS_MEN;
  // biome-ignore lint/correctness/useExhaustiveDependencies: onValidChange is stable from parent
  useEffect(() => {
    onValidChange(!!data.avatarId && data.description.trim().length > 0 && !!data.geoId);
  }, [data.avatarId, data.description, data.geoId]);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label className="text-base font-medium">
          {t('signup.fields.avatar')}
          <RequiredMark required />
        </Label>
        <p className="text-sm text-muted-foreground">{t('signup.hints.avatar')}</p>
        <RadioGroup
          value={data.avatarId ? String(data.avatarId) : ''}
          onValueChange={(value) => onChange({ avatarId: Number(value) })}
          className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-7 gap-2"
        >
          {avatars.map((avatar, index) => (
            <div key={avatar.id} className="relative">
              <RadioGroupItem
                value={String(avatar.id)}
                id={`avatar-${avatar.id}`}
                className="sr-only"
              />
              <Label
                htmlFor={`avatar-${avatar.id}`}
                className={`block cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                  data.avatarId === avatar.id
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-muted hover:border-primary/50'
                }`}
              >
                <div className="aspect-square relative bg-muted">
                  <img
                    src={`/avatars/${avatar.file}`}
                    alt={`Avatar ${index + 1}`}
                    className="object-cover"
                  />
                </div>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">
          {t('signup.fields.description')}
          <RequiredMark required />
        </Label>
        <p className="text-xs text-muted-foreground">{t('signup.hints.description')}</p>
        <Textarea
          id="description"
          placeholder={t('signup.placeholders.description')}
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={4}
          maxLength={1000}
          required
        />
        <p className="text-xs text-muted-foreground text-right">{data.description.length}/1000</p>
      </div>

      <ZipCityPicker
        value={{ zipCode: data.zipCode, geoId: data.geoId, cityLabel: data.cityLabel }}
        onChange={(v) => onChange(v)}
        required
      />
      <p className="text-xs text-muted-foreground">{t('signup.hints.required')}</p>
    </div>
  );
}
